import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useAuth } from '../App';
import { clients as clientsApi } from '../api';
import { Plus, Upload, Download, Eye, EyeOff, Edit2, X, Search } from 'lucide-react';

function ClientModal({ client, office, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(client || { office, pan: '', password: '', dob: '', name: '', mobile: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSave = async () => {
    if (!form.pan || !form.password) return toast.error('PAN and Password are required');
    setLoading(true);
    try {
      if (client?.id) {
        await clientsApi.update(client.id, form);
        toast.success('Client updated');
      } else {
        await clientsApi.create(form);
        toast.success('Client added');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving client');
    } finally {
      setLoading(false);
    }
  };

  const f = (k) => ({ value: form[k] || '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="modal-title" style={{ margin: 0 }}>{client?.id ? t('edit') : t('addClient')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><X size={20} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!client?.id && (
            <div>
              <label className="label">{t('office')}</label>
              <select className="input" {...f('office')}>
                <option value="ahmedabad">{t('ahmedabad')}</option>
                <option value="palanpur">{t('palanpur')}</option>
              </select>
            </div>
          )}
          <div className="grid-2">
            <div>
              <label className="label">{t('pan')} *</label>
              <input className="input" {...f('pan')} style={{ textTransform: 'uppercase' }} maxLength={10} />
            </div>
            <div>
              <label className="label">{t('dob')}</label>
              <input className="input" type="date" {...f('dob')} />
            </div>
          </div>
          <div>
            <label className="label">{t('password')} *</label>
            <div className="password-field">
              <input className="input" type={showPass ? 'text' : 'password'} {...f('password')} style={{ paddingRight: 36 }} />
              <button className="password-toggle" onClick={() => setShowPass(p => !p)} type="button">
                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">{t('name')}</label>
            <input className="input" {...f('name')} />
          </div>
          <div className="grid-2">
            <div>
              <label className="label">{t('mobile')}</label>
              <input className="input" {...f('mobile')} />
            </div>
            <div>
              <label className="label">{t('email')}</label>
              <input className="input" type="email" {...f('email')} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CSVUploadModal({ office, onClose, onDone }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(office);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return toast.error('Please select a CSV file');
    setLoading(true);
    try {
      const res = await clientsApi.uploadCSV(selectedOffice, file);
      setResult(res.data);
      toast.success(`Added ${res.data.added} clients`);
      onDone();
    } catch (err) {
      toast.error('CSV upload failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    const res = await clientsApi.template();
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = 'client_template.csv'; a.click();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="modal-title" style={{ margin: 0 }}>{t('uploadCSV')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><X size={20} /></button>
        </div>
        {!result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="label">{t('office')}</label>
              <select className="input" value={selectedOffice} onChange={e => setSelectedOffice(e.target.value)}>
                <option value="ahmedabad">{t('ahmedabad')}</option>
                <option value="palanpur">{t('palanpur')}</option>
              </select>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}>
              <Download size={14} /> {t('downloadTemplate')}
            </button>
            <div>
              <label className="label">CSV File (PAN, Password, DOB)</label>
              <input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="input" style={{ padding: '6px' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={loading}>
                {loading ? 'Uploading...' : t('uploadCSV')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="alert alert-success">✓ Added: {result.added} clients</div>
            {result.duplicates?.length > 0 && (
              <div className="alert alert-warning">⚠ Duplicates skipped: {result.duplicates.join(', ')}</div>
            )}
            {result.errors?.length > 0 && (
              <div className="alert alert-danger">✗ Errors: {result.errors.map(e => e.pan).join(', ')}</div>
            )}
            <button className="btn btn-primary" onClick={onClose} style={{ marginTop: 12 }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientDatabase() {
  const { t } = useTranslation();
  const { activeOffice } = useAuth();
  const [clientList, setClientList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientsApi.list({ office: activeOffice, search, page, limit: 50 });
      setClientList(res.data.clients);
      setTotal(res.data.total);
    } catch (e) {}
    setLoading(false);
  }, [activeOffice, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [activeOffice, search]);

  const togglePass = (id) => setVisiblePasswords(p => ({ ...p, [id]: !p[id] }));

  const deactivate = async (client) => {
    if (!window.confirm(`Deactivate ${client.pan}?`)) return;
    await clientsApi.update(client.id, { status: 'inactive' });
    toast.success('Client deactivated');
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{t('clientDatabase')}</h1>
          <p style={{ color: 'var(--gray)', fontSize: 13 }}>{total} clients • {activeOffice === 'ahmedabad' ? t('ahmedabad') : t('palanpur')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setShowCSV(true)}>
            <Upload size={15} /> {t('uploadCSV')}
          </button>
          <button className="btn btn-primary" onClick={() => { setEditClient(null); setShowModal(true); }}>
            <Plus size={15} /> {t('addClient')}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', maxWidth: 340 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--gray)' }} />
          <input className="input" style={{ paddingLeft: 34 }} placeholder={`${t('search')} PAN, Name, Code...`}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('clientCode')}</th>
                  <th>{t('pan')}</th>
                  <th>{t('name')}</th>
                  <th>{t('password')}</th>
                  <th>{t('dob')}</th>
                  <th>{t('mobile')}</th>
                  <th>{t('status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {clientList.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>No clients found</td></tr>
                ) : clientList.map(c => (
                  <tr key={c.id}>
                    <td><span className="badge badge-blue">{c.client_code}</span></td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.pan}</td>
                    <td>{c.name || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {visiblePasswords[c.id] ? c.password : '••••••••'}
                        </span>
                        <button onClick={() => togglePass(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}>
                          {visiblePasswords[c.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </td>
                    <td>{c.dob || '—'}</td>
                    <td>{c.mobile || '—'}</td>
                    <td><span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{c.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditClient(c); setShowModal(true); }}>
                          <Edit2 size={12} />
                        </button>
                        {c.status === 'active' && (
                          <button className="btn btn-danger btn-sm" onClick={() => deactivate(c)}>
                            {t('deactivate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span style={{ padding: '5px 12px', fontSize: 13 }}>Page {page} of {Math.ceil(total / 50)}</span>
          <button className="btn btn-secondary btn-sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      {showModal && (
        <ClientModal client={editClient} office={activeOffice} onClose={() => setShowModal(false)} onSaved={load} />
      )}
      {showCSV && (
        <CSVUploadModal office={activeOffice} onClose={() => setShowCSV(false)} onDone={load} />
      )}
    </div>
  );
}
